import { IDockviewPanel, IDockviewPanelProps } from "dockview-react";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./utils/api";
import Plot from "react-plotly.js";
import SafeNumberInput from "./SafeNumberInput";

interface IMultiIVData {
  options: IOptionTopData[];
  underlying: IUndData[];
}

interface IUndData {
  ts_event: string;
  price: number;
  size: number;
}

interface IOptionTopData {
  contract: string;
  data: IOptionPricingData[];
}

interface IOptionPricingData {
  ts_event: string;
  price: number;
  size: number;
  underlying_price: number;
  iv: number;
}

function parseOCC(contract: string): string {
  // OCC format: ROOT (padded to 6 chars) + YYMMDD + C/P + strike (8 digits, /1000)
  // e.g. "SPX   240510C05000000"
  const match = contract.match(/^([A-Z\s]{1,6})(\d{6})([CP])(\d{8})$/);
  if (!match) return contract.trim();

  const root = match[1].trim();
  const dateStr = match[2];
  const right = match[3];
  const strikeRaw = parseInt(match[4]);
  const strike = strikeRaw / 1000;

  const year = 2000 + parseInt(dateStr.substring(0, 2));
  const month = parseInt(dateStr.substring(2, 4)) - 1;
  const day = parseInt(dateStr.substring(4, 6));

  const dateFormatted = new Date(year, month, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const strikeStr = strike % 1 === 0 ? strike.toFixed(0) : strike.toString();
  return `${root} ${strikeStr} ${right} ${dateFormatted}`;
}

function formatDateTime(ts: string): string {
  const d = new Date(ts.replace(" ", "T"));
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }) + " " + d.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const TRACE_COLORS = [
  "green",
  "orange",
  "purple",
  "brown",
  "pink",
  "gray",
  "cyan",
  "magenta",
  "lime",
];

export const MultiIV = (props: IDockviewPanelProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [optChartData, setOptChartData] = useState<any[]>([]);
  const [optLayout, setOptLayout] = useState<any>({});

  const [undChartData, setUndChartData] = useState<any[]>([]);
  const [undLayout, setUndLayout] = useState<any>({});

  const [optIvData, setOptIvData] = useState<any[]>([]);
  const [optIvLayout, setOptIvLayout] = useState<any>({});

  const [optionData, setOptionData] = useState<IOptionTopData[]>([]);
  const [selectedContract, setSelectedContract] = useState<string>("");
  const [buyRows, setBuyRows] = useState<Set<number>>(new Set());
  const [sellRows, setSellRows] = useState<Set<number>>(new Set());
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  useEffect(() => {
    console.log("Multi IV params:", props.params);
    // if all params are present, fetch data
    if (
      props.params?.contracts &&
      props.params?.startDate &&
      props.params?.endDate
    ) {
      fetchData(
        props.params.contracts,
        props.params.startDate,
        props.params.endDate, 
        props.params.showUnderlying
      );
    }
  }, []);

  async function fetchData(
    contracts: string[],
    startDate: string,
    endDate: string,
    showUnderlying: boolean
  ) {
    setIsLoading(true);
    setErrorMessage("");

    let responseData: IMultiIVData | null = null;

    try {
      const resp = await api.post<IMultiIVData>("/multi-iv", {
        contracts,
        startDate,
        endDate,
        showUnderlying
      });
      responseData = resp.data;
    } catch (err: any) {
      setErrorMessage("Request failed");

      return;
    }

    if (!responseData) {
      setErrorMessage("No data received");
      setIsLoading(false);
      return;
    }

    // get x axis ranges from underlying data
    const minX = new Date(responseData.underlying[0].ts_event.replace(" ", "T")); 
    const maxX = new Date(responseData.underlying[responseData.underlying.length - 1].ts_event.replace(" ", "T"));

    // UNDERLYING CHART
    const traceUndTrades = {
      type: "scatter",
      x: responseData.underlying.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.underlying.map((d) => d.price),
      customdata: responseData.underlying.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Ask] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Underlying Trades",
      line: {
        color: "blue",
        width: 1,
      },
      mode: "lines+markers",
      marker: {
        size: 3, // bigger circle size (default is ~6)
        symbol: "circle", // can be "circle", "square", "diamond", etc
      },
    };

    const colors = TRACE_COLORS;

    let optPriceTraces = [];

    for (let i = 0; i < responseData.options.length; i++) {
      const traceName = parseOCC(responseData.options[i].contract);

      const trace = {
        type: "scatter",
        x: responseData.options[i].data.map(
          (d) => new Date(d.ts_event.replace(" ", "T"))
        ),
        y: responseData.options[i].data.map((d) => d.price),
        customdata: responseData.options[i].data.map((d) => [
          d.ts_event,
          d.price,
          d.size,
          d.underlying_price,
          d.iv,
          traceName,
        ]),
        hovertemplate:
          "<b>Time:</b> %{x}<br>" +
          "<b>[Trade] Price:</b> %{y}<br>" +
          "<b>Size:</b> %{customdata[2]}<br>" +
          "<b>Underlying Price:</b> %{customdata[3]}<br>" +
          "<b>IV:</b> %{customdata[4]}<br>" +
          "<b>Contract:</b> %{customdata[5]}<br>" +
          "<extra></extra>", // removes trace name in hover box
        name: responseData.options[i].contract,
        line: {
          color: colors[i % colors.length],
          width: 1,
        },

        mode: "lines+markers",
        marker: {
          size: 3, // bigger circle size (default is ~6)
          symbol: "circle", // can be "circle", "square", "diamond", etc
        },
      };

      // add big points for trades > 25 size
      const bigPointsTrace = {
        type: "scatter",
        mode: "markers",
        x: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => new Date(d.ts_event.replace(" ", "T"))),
        y: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => d.price),
        marker: { size: 4, color: "red", symbol: "circle" },
        customdata: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => [
            d.ts_event,
            d.price,
            d.underlying_price,
            d.size,
            d.iv,
            traceName,
          ]),
        hovertemplate:
          "<b>Time:</b> %{x}<br>" +
          "<b>Price:</b> %{y}<br>" +
          "<b>Underlying Price:</b> %{customdata[2]}<br>" +
          "<b>Size:</b> %{customdata[3]}<br>" +
          "<b>IV:</b> %{customdata[4]}<br>" +
          "<b>Contract:</b> %{customdata[5]}<br>" +
          "<extra></extra>", // removes trace name in hover box
        name: responseData.options[i].contract + " big trades",
      };

      optPriceTraces.push(trace);
      optPriceTraces.push(bigPointsTrace);
    }

    // OPTIONS IV TRACES
    let optIvTraces = [];

    for (let i = 0; i < responseData.options.length; i++) {
      const traceName = parseOCC(responseData.options[i].contract);

      const trace = {
        type: "scatter",
        x: responseData.options[i].data.map(
          (d) => new Date(d.ts_event.replace(" ", "T"))
        ),
        y: responseData.options[i].data.map((d) => d.iv),
        customdata: responseData.options[i].data.map((d) => [
          d.ts_event,
          d.underlying_price,
          d.size,
          traceName,
          d.price,
        ]),
        hovertemplate:
          "<b>Time:</b> %{x}<br>" +
          "<b>IV:</b> %{y}<br>" +
          "<b>Underlying Price:</b> %{customdata[1]}<br>" +
          "<b>Size:</b> %{customdata[2]}<br>" +
          "<b>Contract:</b> %{customdata[3]}<br>" +
          "<b>Price:</b> %{customdata[4]}<br>" +
          "<extra></extra>", // removes trace name in hover box
        name: responseData.options[i].contract,
        line: {
          color: colors[i % colors.length],
          width: 1,
        },

        mode: "lines+markers",
        marker: {
          size: 3, // bigger circle size (default is ~6)
          symbol: "circle", // can be "circle", "square", "diamond", etc
        },
      };

      const bigPointsTrace = {
        type: "scatter",
        mode: "markers",
        x: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => new Date(d.ts_event.replace(" ", "T"))),
        y: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => d.iv),
        marker: { size: 4, color: "red", symbol: "circle" },
        customdata: responseData.options[i].data
          .filter((d) => d.size > 25)
          .map((d) => [
            d.ts_event,
            d.underlying_price,
            d.size,
            traceName,
            d.price,
          ]),
        hovertemplate:
          "<b>Time:</b> %{x}<br>" +
          "<b>IV:</b> %{y}<br>" +
          "<b>Underlying Price:</b> %{customdata[1]}<br>" +
          "<b>Size:</b> %{customdata[2]}<br>" +
          "<b>Contract:</b> %{customdata[3]}<br>" +
          "<b>Price:</b> %{customdata[4]}<br>" +
          "<extra></extra>",
        name: traceName + " big trades",
      };

      optIvTraces.push(trace);
      optIvTraces.push(bigPointsTrace);
    }

    setOptChartData(optPriceTraces);
    setUndChartData([traceUndTrades]);
    setOptIvData(optIvTraces);

    setUndLayout({
      dragmode: "zoom",
      autosize: true,
      margin: { l: 40, r: 90, t: 0, b: 40 },
      showlegend: true,
      legend: {
        x: 1, // left side
        y: 0, // bottom
        xanchor: "right",
        yanchor: "bottom",
      },

      hovermode: "closest", // crosshairs
      hoverdistance: 25,
      xaxis: {
        autorange: false,
        range: [minX, maxX],
        domain: [0, 1],
        rangeslider: { visible: false },
        type: "date",
        // crosshairs
        showspikes: true,
        spikesnap: "cursor",
        spikecolor: "#999",
        spikethickness: 1,
        spikemode: "toaxis+across+marker",
        spikedash: "solid",
        showgrid: true,
      },
      yaxis: {
        autorange: true,
        domain: [0, 1],
        type: "linear",
        // crosshairs
        showspikes: true,
        spikemode: "toaxis+across+marker",
        spikesnap: "cursor",
        spikedash: "solid",
        spikecolor: "#999",
        spikethickness: 1,
        showgrid: true,
      },
    });

    setOptLayout({
      dragmode: "zoom",
      autosize: true,
      margin: { l: 40, r: 90, t: 0, b: 40 },
      showlegend: true,
      legend: {
        x: 1, // left side
        y: 0, // bottom
        xanchor: "right",
        yanchor: "bottom",
      },

      hovermode: "closest", // crosshairs
      hoverdistance: 25,
      xaxis: {
        autorange: false,
        range: [minX, maxX],
        domain: [0, 1],
        rangeslider: { visible: false },
        type: "date",
        // crosshairs
        showspikes: true,
        spikesnap: "cursor",
        spikecolor: "#999",
        spikethickness: 1,
        spikemode: "toaxis+across+marker",
        spikedash: "solid",
        showgrid: true,
      },
      yaxis: {
        autorange: true,
        domain: [0, 1],
        type: "linear",
        // crosshairs
        showspikes: true,
        spikemode: "toaxis+across+marker",
        spikesnap: "cursor",
        spikedash: "solid",
        spikecolor: "#999",
        spikethickness: 1,
        showgrid: true,
      },
    });

    setOptIvLayout({
      dragmode: "zoom",
      autosize: true,
      margin: { l: 40, r: 90, t: 0, b: 40 },
      showlegend: true,
      legend: {
        x: 1, // left side
        y: 0, // bottom
        xanchor: "right",
        yanchor: "bottom",
      },

      hovermode: "closest", // crosshairs
      hoverdistance: 25,
      xaxis: {
        autorange: false,
        range: [minX, maxX],
        domain: [0, 1],
        rangeslider: { visible: false },
        type: "date",
        // crosshairs
        showspikes: true,
        spikesnap: "cursor",
        spikecolor: "#999",
        spikethickness: 1,
        spikemode: "toaxis+across+marker",
        spikedash: "solid",
        showgrid: true,
      },
      yaxis: {
        autorange: true,
        domain: [0, 1],
        type: "linear",
        // crosshairs
        showspikes: true,
        spikemode: "toaxis+across+marker",
        spikesnap: "cursor",
        spikedash: "solid",
        spikecolor: "#999",
        spikethickness: 1,
        showgrid: true,
      },
    });

    setOptionData(responseData.options);
    if (responseData.options.length > 0) {
      setSelectedContract(responseData.options[0].contract);
    }

    setIsLoading(false);
  }

  const selectedData = optionData.find((o) => o.contract === selectedContract);

  function toggleBuy(i: number) {
    setBuyRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); return next; }
      next.add(i);
      setSellRows((s) => { const ns = new Set(s); ns.delete(i); return ns; });
      return next;
    });
  }

  function toggleSell(i: number) {
    setSellRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); return next; }
      next.add(i);
      setBuyRows((b) => { const nb = new Set(b); nb.delete(i); return nb; });
      return next;
    });
  }

  function toggleAllBuy() {
    if (!selectedData) return;
    const allIdx = new Set(selectedData.data.map((_, i) => i));
    if (buyRows.size === selectedData.data.length) {
      setBuyRows(new Set());
    } else {
      setBuyRows(allIdx);
      setSellRows(new Set());
    }
  }

  function toggleAllSell() {
    if (!selectedData) return;
    const allIdx = new Set(selectedData.data.map((_, i) => i));
    if (sellRows.size === selectedData.data.length) {
      setSellRows(new Set());
    } else {
      setSellRows(allIdx);
      setBuyRows(new Set());
    }
  }

  function calcStats(rows: Set<number>, data: IOptionPricingData[]) {
    if (rows.size === 0) return null;
    let vol = 0, priceVol = 0, undVol = 0;
    for (const i of Array.from(rows)) {
      vol      += data[i].size;
      priceVol += data[i].price * data[i].size;
      undVol   += data[i].underlying_price * data[i].size;
    }
    const vwap = priceVol / vol;
    return { vwap, undVwap: undVol / vol, volume: vol, estCost: vol * vwap * 100 };
  }

  const buyStats  = selectedData ? calcStats(buyRows,  selectedData.data) : null;
  const sellStats = selectedData ? calcStats(sellRows, selectedData.data) : null;

  const selectionTraces = useMemo(() => {
    if (!selectedData) return { und: [], opt: [], iv: [] };

    function buildTraces(rows: Set<number>, symbol: string, name: string) {
      if (rows.size === 0) return null;

      // Sort indices chronologically so cumulative stats accumulate in order
      const sortedIdx = Array.from(rows).sort((a, b) => a - b);

      // Running cumulative stats
      let vol = 0, priceVol = 0, undVol = 0;
      const pts = sortedIdx.map((i) => {
        const d = selectedData!.data[i];
        vol      += d.size;
        priceVol += d.price * d.size;
        undVol   += d.underlying_price * d.size;
        const vwap = priceVol / vol;
        return {
          x:        new Date(d.ts_event.replace(" ", "T")),
          und:      d.underlying_price,
          price:    d.price,
          iv:       d.iv,
          custom:   [
            formatDateTime(d.ts_event),
            d.underlying_price,
            d.price,
            d.size,
            d.iv.toFixed(3),
            vwap.toFixed(2),
            vol,
            (undVol / vol).toFixed(2),
            (vol * vwap * 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          ],
        };
      });

      const xs         = pts.map((p) => p.x);
      const customdata = pts.map((p) => p.custom);
      const mk         = { size: 10, color: "yellow", symbol, line: { color: "black", width: 1 } };
      const ht         =
        "Date: %{customdata[0]}<br>" +
        "Und: %{customdata[1]}<br>" +
        "Price: %{customdata[2]}<br>" +
        "Size: %{customdata[3]}<br>" +
        "IV: %{customdata[4]}<br>" +
        "VWAP: %{customdata[5]}<br>" +
        "Volume: %{customdata[6]}<br>" +
        "Und VWAP: %{customdata[7]}<br>" +
        "Est cost: %{customdata[8]}<extra></extra>";

      return {
        und: { type: "scatter", mode: "markers", x: xs, y: pts.map((p) => p.und),   customdata, hovertemplate: ht, marker: mk, name, showlegend: true },
        opt: { type: "scatter", mode: "markers", x: xs, y: pts.map((p) => p.price), customdata, hovertemplate: ht, marker: mk, name, showlegend: true },
        iv:  { type: "scatter", mode: "markers", x: xs, y: pts.map((p) => p.iv),    customdata, hovertemplate: ht, marker: mk, name, showlegend: true },
      };
    }

    const buy  = buildTraces(buyRows,  "triangle-up", "Buy");
    const sell = buildTraces(sellRows, "square",      "Sell");
    return {
      und: [...(buy  ? [buy.und]  : []), ...(sell ? [sell.und] : [])],
      opt: [...(buy  ? [buy.opt]  : []), ...(sell ? [sell.opt] : [])],
      iv:  [...(buy  ? [buy.iv]   : []), ...(sell ? [sell.iv]  : [])],
    };
  }, [selectedData, buyRows, sellRows]);

  return (
    <div className="overflow-y-scroll h-full p-2">
      {errorMessage && (
        <p className="text-red-500 font-weight-bold">Error: {errorMessage}</p>
      )}

      <div>
        <h1>Underlying:</h1>
        <Plotter data={[...undChartData, ...selectionTraces.und]} layout={undLayout} isLoading={isLoading} />
      </div>

      <div>
        <h1>Options:</h1>
        <Plotter data={[...optChartData, ...selectionTraces.opt]} layout={optLayout} isLoading={isLoading} />
      </div>

      <div>
        <h1>IV:</h1>
        <Plotter data={[...optIvData, ...selectionTraces.iv]} layout={optIvLayout} isLoading={isLoading} />
      </div>

      {optionData.length > 0 && (
        <div className="mt-4">
          <h1>Option Trades:</h1>

          {/* Contract selector */}
          <div className="flex flex-wrap gap-2 mb-3">
            {optionData.map((opt, idx) => {
              const color = TRACE_COLORS[idx % TRACE_COLORS.length];
              const isActive = selectedContract === opt.contract;
              return (
                <button
                  key={opt.contract}
                  type="button"
                  onClick={() => {
                    setSelectedContract(opt.contract);
                    setBuyRows(new Set());
                    setSellRows(new Set());
                  }}
                  style={{
                    borderColor: color,
                    color: isActive ? "#fff" : color,
                    backgroundColor: isActive ? color : "transparent",
                  }}
                  className="px-3 py-1 rounded border text-sm"
                >
                  {parseOCC(opt.contract)}
                </button>
              );
            })}
          </div>

          {/* Trades grid */}
          {selectedData && (
            <>
              {/* grid cols: B-cb | S-cb | date | und | price | size | iv | [divider] VWAP | Volume | Und VWAP | Est cost */}
              <div className="grid [grid-template-columns:auto_auto_1fr_auto_auto_auto_auto_auto_auto_auto_auto] text-sm text-black overflow-x-auto">
                {/* Header */}
                <div className="py-1 pr-2 border-b border-gray-400 text-green-700">
                  <input type="checkbox" title="Select all as Buy"
                    checked={buyRows.size === selectedData.data.length && selectedData.data.length > 0}
                    onChange={toggleAllBuy} />
                  <span className="ml-1">B</span>
                </div>
                <div className="py-1 pr-4 border-b border-gray-400 text-red-700">
                  <input type="checkbox" title="Select all as Sell"
                    checked={sellRows.size === selectedData.data.length && selectedData.data.length > 0}
                    onChange={toggleAllSell} />
                  <span className="ml-1">S</span>
                </div>
                <div className="py-1 pr-4 border-b border-gray-400">Date &amp; Time</div>
                <div className="py-1 pr-4 border-b border-gray-400">Und</div>
                <div className="py-1 pr-4 border-b border-gray-400">Price</div>
                <div className="py-1 pr-4 border-b border-gray-400">Size</div>
                <div className="py-1 pr-6 border-b border-gray-400">IV</div>
                <div className="py-1 pr-4 border-b border-gray-400 border-l border-l-gray-400 pl-4">VWAP</div>
                <div className="py-1 pr-4 border-b border-gray-400">Volume</div>
                <div className="py-1 pr-4 border-b border-gray-400">Und VWAP</div>
                <div className="py-1 border-b border-gray-400">Est cost</div>

                {/* Rows */}
                {selectedData.data.map((row, i) => {
                  const isBuy  = buyRows.has(i);
                  const isSell = sellRows.has(i);
                  const isHovered = hoveredRow === i;
                  const bg = isBuy ? "bg-green-100" : isSell ? "bg-red-100" : isHovered ? "bg-gray-50" : "";
                  const hover = { onMouseEnter: () => setHoveredRow(i), onMouseLeave: () => setHoveredRow(null) };

                  // Cumulative stats up to row i for the active track
                  let cumVwap = "", cumVol = "", cumUndVwap = "", cumEst = "";
                  const trackSet = isBuy ? buyRows : isSell ? sellRows : null;
                  if (trackSet) {
                    let vol = 0, priceVol = 0, undVol = 0;
                    for (let j = 0; j <= i; j++) {
                      if (trackSet.has(j)) {
                        vol      += selectedData.data[j].size;
                        priceVol += selectedData.data[j].price * selectedData.data[j].size;
                        undVol   += selectedData.data[j].underlying_price * selectedData.data[j].size;
                      }
                    }
                    const vwap = priceVol / vol;
                    cumVwap    = vwap.toFixed(2);
                    cumVol     = String(vol);
                    cumUndVwap = (undVol / vol).toFixed(2);
                    cumEst     = (vol * vwap * 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  }

                  const cell = (key: string, content: React.ReactNode, extra = "") =>
                    <div key={key} className={`py-1 pr-4 border-b border-gray-200 ${extra} ${bg}`} {...hover}>{content}</div>;

                  return [
                    <div key={`buy-${i}`} className={`py-1 pr-2 border-b border-gray-200 ${bg}`} {...hover}>
                      <input type="checkbox" title="Buy" checked={isBuy}
                        onChange={() => toggleBuy(i)} onClick={(e) => e.stopPropagation()} />
                    </div>,
                    <div key={`sell-${i}`} className={`py-1 pr-4 border-b border-gray-200 ${bg}`} {...hover}>
                      <input type="checkbox" title="Sell" checked={isSell}
                        onChange={() => toggleSell(i)} onClick={(e) => e.stopPropagation()} />
                    </div>,
                    cell(`dt-${i}`,  formatDateTime(row.ts_event)),
                    cell(`up-${i}`,  row.underlying_price),
                    cell(`pr-${i}`,  row.price),
                    cell(`sz-${i}`,  row.size),
                    cell(`iv-${i}`,  row.iv.toFixed(3), "pr-6"),
                    cell(`cv-${i}`,  cumVwap,    "border-l border-l-gray-300 pl-4"),
                    cell(`cvl-${i}`, cumVol),
                    cell(`cu-${i}`,  cumUndVwap),
                    cell(`ce-${i}`,  cumEst,     "pr-0"),
                  ];
                })}
              </div>

              <div className="mt-3 text-sm text-black space-y-1">
                {buyStats && (
                  <div className="flex gap-6">
                    <span className="text-green-700">Buy track:</span>
                    <span>VWAP: {buyStats.vwap.toFixed(2)}</span>
                    <span>Volume: {buyStats.volume}</span>
                    <span>Und VWAP: {buyStats.undVwap.toFixed(2)}</span>
                    <span>Est. cost: {buyStats.estCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                {sellStats && (
                  <div className="flex gap-6">
                    <span className="text-red-700">Sell track:</span>
                    <span>VWAP: {sellStats.vwap.toFixed(2)}</span>
                    <span>Volume: {sellStats.volume}</span>
                    <span>Und VWAP: {sellStats.undVwap.toFixed(2)}</span>
                    <span>Est. cost: {sellStats.estCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Plotter = (props: { data: any[]; layout: any; isLoading: boolean }) => {
  // deep copy: Plotly writes zoom/pan ranges into the layout's nested objects, so each chart
  // needs its own - never hand it a layout another chart could also be using
  const layout = React.useMemo(() => structuredClone(props.layout), [props.layout]);
  if (props.isLoading) {
    return <div className="text-gray-500">Loading...</div>;
  }

  if (props.data.length === 0) {
    return <p className="text-gray-500">No data to display</p>;
  }

  return (
    <Plot
      data={props.data}
      layout={layout}
      useResizeHandler={true}
      style={{
        width: "100%",
      }}
    />
  );
};
